/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/enterprise/credentials/CredentialsPanel" />
import * as React from "react";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { TextField } from "VSSUI/TextField";
import { KeyCode } from "VSSUI/Util";
import { showConfirmationDialog } from "Wit/Settings/BoardsExternalIntegration/components/common";
import { BasePanel } from "Wit/Settings/BoardsExternalIntegration/components/panels/BasePanel";
import { ICredentialsPanelPanelProps } from "Wit/Settings/BoardsExternalIntegration/components/panels/enterprise/credentials/CredentialsPanel.Props";
import { EnterpriseServerUrl } from "Wit/Settings/BoardsExternalIntegration/components/panels/enterprise/EnterpriseServerUrl";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";
import { createEnterpriseCredentialsRequest, isValidEnterpriseUrl } from "Wit/Settings/BoardsExternalIntegration/utils";

// TODO: Move errorMessage into a common, parent interface.
// TODO: Possibly move spinner into something reusable.
export interface ICredentialsPanelPanelState {
    isDirty: boolean;
    connectionErrorMessage?: string;
    loadingSpinnerMessage?: string;
    accceptUntrustedSsl: boolean;
    url: string;
    urlErrorMessage?: string;
    username: string;
    password: string;
}

export class CredentialsPanel extends VssComponent<ICredentialsPanelPanelProps, ICredentialsPanelPanelState, IVssComponentContext> {
    private _isMounted = false;
    private _isConfirmationDialogOpened = false;

    constructor(props: ICredentialsPanelPanelProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            isDirty: false,
            connectionErrorMessage: undefined,
            loadingSpinnerMessage: undefined,
            url: "",
            accceptUntrustedSsl: false,
            username: "",
            password: ""
        };
    }

    public render() {
        return (
            <BasePanel
                onDismiss={this._onDismiss}
                title={Resources.EnterpriseCredentials_Title}
                onRenderHeaderContent={this._renderHeader}
                onRenderPanelFooter={this._renderFooter}
                errorMessage={this.state.connectionErrorMessage}
                onDismissErrorMessage={this._onDismissErrorMessage}
                loadingSpinnerMessage={this.state.loadingSpinnerMessage}
                className="form-panel"
            >
                <div className="form-panel-content">
                    <EnterpriseServerUrl
                        url={this.state.url}
                        acceptUntrustedSsl={this.state.accceptUntrustedSsl}
                        errorMessage={this.state.urlErrorMessage}
                        onEnterpriseUrlInputChanged={this._onEnterpriseUrlInputChanged}
                        onSslChange={this._onSslCheckBoxChanged}
                        onKeyDown={this._onKeyDown}
                    />
                    <div className="input-label fontWeightSemiBold">{Resources.EnterpriseCredentials_Username}</div>
                    <TextField
                        ariaLabel={Resources.EnterpriseCredentials_Username}
                        className="input-field"
                        onChange={this._onUsernameInputChanged}
                        placeholder={Resources.EnterpriseCredentials_UsernamePlaceholder}
                        maxLength={300}
                        value={this.state.username}
                        onKeyDown={this._onKeyDown}
                    />
                    <div className="input-label fontWeightSemiBold">{Resources.EnterpriseCredentials_Password}</div>
                    <TextField
                        ariaLabel={Resources.EnterpriseCredentials_Password}
                        className="input-field"
                        onChange={this._onPasswordInputChanged}
                        placeholder={Resources.EnterpriseCredentials_PasswordPlaceholder}
                        maxLength={300}
                        inputType="password"
                        value={this.state.password}
                        onKeyDown={this._onKeyDown}
                    />
                </div>
            </BasePanel>
        );
    }

    public componentDidMount(): void {
        this._isMounted = true;
    }

    public componentWillUnmount(): void {
        this._isMounted = false;
    }

    private _renderHeader = (): JSX.Element => {
        return <div>{Resources.EnterpriseCredentials_SubTitle}</div>;
    };

    private _renderFooter = (): JSX.Element => {
        return (
            <div className="align-right">
                <Button text={Resources.Cancel} onClick={this.props.onDismiss} />
                <Button primary={true} text={Resources.Connect} onClick={this._onConnectButtonClick} disabled={!this._canConnect()} />
            </div>
        );
    };

    private _onDismiss = (): void => {
        if (!this.state.isDirty) {
            this.props.onDismiss();
        } else {
            showConfirmationDialog(
                this.context,
                Resources.ConfirmationClosePanelMessage,
                Resources.ConfirmationClosePanelActionText,
                Resources.ConfirmationClosePanelTitle,
                this.props.onDismiss,
                this._onConfirmationDialogCanceled
            );
            this._isConfirmationDialogOpened = true;
            this.setState({ loadingSpinnerMessage: undefined });
        }
    };

    private _onConfirmationDialogCanceled = (): void => {
        this._isConfirmationDialogOpened = false;
    };

    private _canConnect = (): boolean => {
        const { url, username, password, urlErrorMessage, connectionErrorMessage } = this.state;
        return !!(url && username && password) && !urlErrorMessage && !connectionErrorMessage;
    };

    private _onConnectButtonClick = () => {
        this.setState({ loadingSpinnerMessage: Resources.SpinnerConnectingAccountText });
        this._authenticate();
    };

    private _authenticate = async (): Promise<void> => {
        const { existingServiceConnections, onConnectionCreated } = this.props;
        const { url, username, password, accceptUntrustedSsl } = this.state;

        try {
            const serviceEndpoint = await createEnterpriseCredentialsRequest(
                this.context.pageContext,
                username,
                password,
                existingServiceConnections,
                url,
                accceptUntrustedSsl
            );
            this._shouldProceed() && onConnectionCreated(serviceEndpoint);
        } catch (error) {
            this._shouldProceed() &&
                this.setState({
                    loadingSpinnerMessage: undefined,
                    connectionErrorMessage: error && error.message
                });
        }
    };

    private _shouldProceed(): boolean {
        return this._isMounted && !this._isConfirmationDialogOpened;
    }

    private _onEnterpriseUrlInputChanged = (value: string): void => {
        const isValidUrlInput = isValidEnterpriseUrl(value);

        this.setState({
            isDirty: true,
            url: value,
            urlErrorMessage: isValidUrlInput ? undefined : Resources.EnterprisePat_InvalidUrlInput,
            connectionErrorMessage: undefined
        });
    };

    private _onKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.keyCode === KeyCode.enter && this._canConnect()) {
            this._onConnectButtonClick();
        }
    };

    private _onSslCheckBoxChanged = (checked: boolean): void => {
        this.setState({
            isDirty: true,
            accceptUntrustedSsl: checked,
            connectionErrorMessage: undefined
        });
    };

    private _onUsernameInputChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string): void => {
        this.setState({
            isDirty: true,
            username: value,
            connectionErrorMessage: undefined
        });
    };

    private _onPasswordInputChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string): void => {
        this.setState({
            isDirty: true,
            password: value,
            connectionErrorMessage: undefined
        });
    };

    private _onDismissErrorMessage = () => {
        this.setState({ connectionErrorMessage: undefined });
    };
}
